import Swal from 'sweetalert2'
import {
  buildRewardDetailModalHtml,
  type RewardDetailModalInput,
} from '@huay/shared/points/rewardDetailModalContent'
import '@huay/shared/points/rewardDetailModal.css'
import {
  buildPointsLedgerText,
  escapeRewardHtml,
  formatRewardPoints,
  type RewardRedeemFlowHandlers,
  type RewardRedeemItem,
  type RewardShippingInfo,
} from '@huay/shared/points/rewardRedeemFlow'

export const CLASSIC_SWAL_CLASS = {
  popup: 'classic-reward-swal-popup',
  title: 'classic-reward-swal-title',
  htmlContainer: 'classic-reward-swal-html',
  actions: 'classic-reward-swal-actions',
  confirmButton: 'classic-reward-swal-confirm',
  cancelButton: 'classic-reward-swal-cancel',
  validationMessage: 'classic-reward-swal-validation',
} as const

function buildLedgerHtml(currentPoints: number, requiredPoints: number): string {
  const ledger = buildPointsLedgerText(currentPoints, requiredPoints)
  return `
    <div class="classic-reward-ledger">
      <span><small>คะแนนปัจจุบัน</small><strong>${ledger.currentLabel}</strong></span>
      <span class="classic-reward-ledger__arrow">→</span>
      <span><small>ใช้แลก</small><strong>${ledger.costLabel}</strong></span>
      <span class="classic-reward-ledger__arrow">→</span>
      <span><small>คงเหลือ</small><strong>${ledger.remainingLabel}</strong></span>
    </div>
  `
}

function buildShippingHtml(shippingInfo: RewardShippingInfo | null): string {
  if (!shippingInfo) return ''
  return `
    <div class="classic-reward-shipping">
      <p class="classic-reward-shipping__title">ข้อมูลจัดส่ง</p>
      <p>ชื่อผู้รับ: <strong>${escapeRewardHtml(shippingInfo.name)}</strong></p>
      <p>เบอร์ติดต่อ: <strong>${escapeRewardHtml(shippingInfo.phone)}</strong></p>
      ${
        shippingInfo.address
          ? `<p>ที่อยู่: <strong>${escapeRewardHtml(shippingInfo.address)}</strong></p>`
          : ''
      }
    </div>
  `
}

const SHIPPING_FORM_HTML = `
  <div class="classic-reward-form">
    <input id="reward-contact-name" class="swal2-input classic-reward-input" placeholder="ชื่อผู้รับ">
    <input id="reward-contact-phone" class="swal2-input classic-reward-input" placeholder="เบอร์ติดต่อ">
    <textarea id="reward-contact-address" class="swal2-textarea classic-reward-input" placeholder="ที่อยู่/หมายเหตุ" rows="3"></textarea>
  </div>
`

export function createClassicRewardRedeemHandlers(): RewardRedeemFlowHandlers {
  return {
    onInsufficientPoints: async ({ currentPoints, requiredPoints }) => {
      await Swal.fire({
        title: 'คะแนนไม่เพียงพอ',
        html: `
          <p>คุณมี <strong>${formatRewardPoints(currentPoints)}</strong> คะแนน</p>
          <p>ต้องการ <strong>${formatRewardPoints(requiredPoints)}</strong> คะแนน</p>
          ${buildLedgerHtml(currentPoints, requiredPoints)}
        `,
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: CLASSIC_SWAL_CLASS,
      })
    },
    onConfirmRedeem: async ({ reward, currentPoints }) => {
      const result = await Swal.fire({
        title: 'ยืนยันการแลกรางวัล',
        html: `
          <p>ใช้ <strong>${formatRewardPoints(reward.pointsCost)}</strong> คะแนน</p>
          <p>เพื่อแลก <strong>${escapeRewardHtml(reward.name)}</strong></p>
          ${
            reward.type === 'credit'
              ? `<p>ได้เครดิต <strong>${formatRewardPoints(reward.creditAmount || 0)}</strong> บาท</p>`
              : ''
          }
          ${buildLedgerHtml(currentPoints, reward.pointsCost)}
        `,
        showCancelButton: true,
        confirmButtonText: 'ดำเนินการต่อ',
        cancelButtonText: 'ยกเลิก',
        buttonsStyling: false,
        customClass: CLASSIC_SWAL_CLASS,
      })
      return result.isConfirmed
    },
    onShippingForm: async () => {
      const result = await Swal.fire({
        title: 'ข้อมูลสำหรับจัดส่ง/รับรางวัล',
        html: SHIPPING_FORM_HTML,
        showCancelButton: true,
        confirmButtonText: 'ดำเนินการต่อ',
        cancelButtonText: 'ยกเลิก',
        buttonsStyling: false,
        customClass: CLASSIC_SWAL_CLASS,
        preConfirm: () => {
          const name = (document.getElementById('reward-contact-name') as HTMLInputElement | null)?.value?.trim()
          const phone = (document.getElementById('reward-contact-phone') as HTMLInputElement | null)?.value?.trim()
          const address = (document.getElementById('reward-contact-address') as HTMLTextAreaElement | null)?.value?.trim()
          if (!name || !phone) {
            Swal.showValidationMessage('กรุณาระบุชื่อผู้รับและเบอร์ติดต่อ')
            return null
          }
          return { name, phone, address }
        },
      })
      if (!result.isConfirmed || !result.value) return null
      return result.value as RewardShippingInfo
    },
    onFinalSummary: async ({ reward, currentPoints, requiredPoints, shippingInfo }) => {
      const result = await Swal.fire({
        title: 'ยืนยันการแลกรางวัล',
        html: `
          <p>รางวัล <strong>${escapeRewardHtml(reward.name)}</strong></p>
          ${buildLedgerHtml(currentPoints, requiredPoints)}
          ${buildShippingHtml(shippingInfo)}
        `,
        showCancelButton: true,
        confirmButtonText: 'ยืนยันแลกรางวัล',
        cancelButtonText: 'ยกเลิก',
        buttonsStyling: false,
        customClass: CLASSIC_SWAL_CLASS,
      })
      return result.isConfirmed
    },
    onSuccess: async ({ message, reward }) => {
      await Swal.fire({
        title: message,
        text: reward.type === 'item'
          ? 'ระบบบันทึกคำขอแลกรางวัลแล้ว กรุณารอแอดมินดำเนินการ'
          : 'คะแนนถูกหักและเครดิตถูกเพิ่มเรียบร้อยแล้ว',
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: CLASSIC_SWAL_CLASS,
      })
    },
    onError: async (message) => {
      await Swal.fire({
        title: 'แลกรางวัลไม่สำเร็จ',
        text: message,
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: CLASSIC_SWAL_CLASS,
      })
    },
  }
}

export async function showClassicRewardDetailModal(reward: RewardDetailModalInput): Promise<void> {
  const html = buildRewardDetailModalHtml(reward, escapeRewardHtml)
  if (!html) return
  await Swal.fire({
    title: 'รายละเอียดรางวัล',
    html,
    confirmButtonText: 'ปิด',
    buttonsStyling: false,
    customClass: CLASSIC_SWAL_CLASS,
  })
}
